/**
 * Shared types for Azure resource discovery ("Browse Azure…"): enumerating a
 * signed-in Azure account's ADX clusters, App Insights components, Log
 * Analytics workspaces, Azure SQL servers/managed instances, PostgreSQL and
 * MySQL flexible servers and storage accounts, then mapping each to a
 * ready-to-use connection draft.
 *
 * Kept in `src/types` because these cross the Electron <-> renderer IPC
 * boundary (`window.electronAPI.azureDiscovery.*`).
 *
 * See `Docs/AzureDiscovery.md` (identity ownership, reliability, completeness —
 * Option C slice C4a) and `Docs/superpowers/specs/2026-07-09-azure-resource-picker-design.md`
 * for the original design.
 */

import type { AzureStorageEndpoints, StorageService } from './azureStorage';

/** Azure resource kinds the picker can discover and map to a connection. */
export type AzureResourceKind =
  | 'adx-cluster'
  | 'app-insights'
  | 'log-analytics'
  | 'sql-server'
  | 'sql-managed-instance'
  | 'pg-flexible'
  | 'mysql-flexible'
  | 'storage-account';

/** Jam engine a discovered resource kind maps onto. */
export type AzureDiscoveryEngine = 'kusto' | 'mssql' | 'pgsql' | 'mysql' | 'azurestorage';

/** Which identity enumerated the account: az CLI or Entra browser token. */
export type AzureIdentitySource = 'az-cli' | 'entra';

/** The identity currently used (or available) for discovery. */
export interface AzureIdentityInfo {
  source: AzureIdentitySource;
  /** Display value ('user@tenant') when known. Never compared — shown in the chip only. */
  account?: string;
  tenantId?: string;
  /** Entra only: MSAL's `<uid>.<utid>` from the saved AuthenticationRecord. */
  homeAccountId?: string;
  /** entra=true when a cached token exists. */
  available: { azCli: boolean; entra: boolean };
}

/**
 * The principal that owns a set of rows: the `oid` + `tid` claims of the one
 * bearer token the listing operation used. Display names are never part of it.
 */
export interface AzureDiscoveryPrincipal {
  source: AzureIdentitySource;
  oid: string;
  tid: string;
}

/** A ready-to-use connection draft prefilled from a discovered resource. */
export interface AzureConnectionDraft {
  server: string;
  /** '0' kusto + azurestorage, '1433' mssql, '5432' pgsql, '3306' mysql. */
  port: string;
  database?: string;
  authentication: 'Azure CLI' | 'Microsoft Entra ID (Interactive Browser)' | 'Password';
  ssl?: boolean;
  /** SQL Server / Managed Instance: Azure requires an encrypted connection with a verified certificate. */
  encrypt?: boolean;
  trustServerCertificate?: boolean;
  /** Storage accounts: the data-plane URLs Resource Graph reported (trailing slash trimmed). */
  azureStorageEndpoints?: AzureStorageEndpoints;
  /** Storage accounts: the services the account publishes an endpoint for. */
  azureStorageServices?: StorageService[];
  /**
   * The ARM resource id of the picked resource — carried for EVERY kind.
   * Persisted as `Connection.azureResourceId` (origin `'picker'`) for the
   * "Open in Azure portal" action; dial-relevant only for storage account-key
   * data access, where it names the account whose key is read.
   */
  armResourceId?: string;
  /**
   * Hierarchical namespace (ADLS Gen2) as Resource Graph reports it — `true` or
   * absent, never `false`. Informs the alias suggestion / badge only: the
   * account-info probe decides HNS at connect time, so it is NOT persisted.
   */
  hns?: boolean;
}

/** SQL Managed Instance endpoint alternatives. `vnet` is always the default. */
export type AzureManagedInstanceEndpoint = 'vnet' | 'public';

/** One alternative endpoint a resource offers (today: the Managed Instance public endpoint). */
export interface AzureEndpointChoice {
  id: AzureManagedInstanceEndpoint;
  label: string;
  server: string;
  port: string;
}

/** One Azure resource mapped to a connectable draft. */
export interface AzureDiscoveredResource {
  /** Full ARM resource id. */
  id: string;
  kind: AzureResourceKind;
  engine: AzureDiscoveryEngine;
  name: string;
  subscriptionId: string;
  subscriptionName: string;
  resourceGroup: string;
  location: string;
  connectionDraft: AzureConnectionDraft;
  /**
   * Alternative endpoints the user may CHOOSE instead of `connectionDraft`'s
   * (a Managed Instance with its public endpoint enabled: `<mi>.public.<zone>.database.windows.net:3342`).
   * The draft keeps the VNet endpoint; a choice is applied only when the user picks it.
   */
  endpointChoices?: AzureEndpointChoice[];
}

/** Typed discovery error surfaced to the picker's error panel. */
export type AzureDiscoveryErrorCode =
  | 'no-identity'
  | 'az-not-found'
  | 'auth-expired'
  | 'mfa-required'
  | 'consent-required'
  | 'conditional-access'
  | 'sign-in-cancelled'
  | 'identity-changed'
  | 'identity-unverifiable'
  | 'no-subscriptions'
  | 'forbidden'
  | 'throttled'
  | 'timeout'
  | 'cancelled'
  | 'network'
  | 'unknown';

export interface AzureDiscoveryError {
  code: AzureDiscoveryErrorCode;
  /** Raw Azure/az message for the error panel (already free of tokens). */
  message: string;
}

/** Why a list is incomplete. */
export type AzureDiscoveryIncompleteReason = 'page_cap' | 'deadline' | 'throttled' | 'cancelled' | 'batch_failed';

/** Whether a list holds every resource the identity can see, and if not, why. */
export interface AzureDiscoveryCompleteness {
  complete: boolean;
  reason?: AzureDiscoveryIncompleteReason;
  /** Mapped resources returned. */
  shown: number;
  /** ARG's `totalRecords` when it reported one. */
  total?: number;
}

/** Subscription scope of a listing. `'all'` is the only value in R1; C4b adds a selection. */
export interface AzureDiscoveryScope {
  subscriptions: 'all';
}

/** `azure-discovery:list` request. */
export interface AzureDiscoveryListRequest {
  source?: AzureIdentitySource;
  /** Whether the Entra browser flow may open for this fetch. */
  interactive?: boolean;
  /** The generation the caller resolved; the list refuses with `identity-changed` when it moved. */
  identityGeneration?: number;
  scope?: AzureDiscoveryScope;
  /** Correlates the operation with `azure-discovery:cancel`. */
  requestId?: string;
}

/** Result of a discovery `list` call — never throws across IPC. */
export type AzureDiscoveryListResult =
  | {
      ok: true;
      identity: AzureIdentityInfo;
      identityGeneration: number;
      /** Owner of every row: the operation's one token. */
      owner: AzureDiscoveryPrincipal;
      resources: AzureDiscoveredResource[];
      completeness: AzureDiscoveryCompleteness;
    }
  | { ok: false; error: AzureDiscoveryError };

/** Result of `azure-discovery:resolve-identity`. */
export type AzureDiscoveryResolveIdentityResult =
  | {
      ok: true;
      identity: AzureIdentityInfo;
      identityGeneration: number;
      /** The principal derived silently (CLI token cache / MSAL silent). Absent when the derivation failed. */
      principal?: AzureDiscoveryPrincipal;
      /** Why the principal could not be derived (classified: `auth-expired`, `network`, …), so cached rows show a recovery instead of waiting forever. */
      principalError?: AzureDiscoveryError;
    }
  | { ok: false; error: AzureDiscoveryError };

/** One Resource Graph row, as returned by the `resources` KQL projection. */
export interface AzureRawResource {
  id: string;
  name: string;
  type: string;
  location: string;
  resourceGroup: string;
  subscriptionId: string;
  properties: Record<string, unknown>;
}

/** Rows owned by `owner` may be confirmed only while the current principal is that same `{ source, oid, tid }`. */
export function samePrincipal(
  owner: AzureDiscoveryPrincipal | undefined,
  current: AzureDiscoveryPrincipal | undefined
): boolean {
  if (!owner || !current) return false;
  return owner.source === current.source && owner.oid === current.oid && owner.tid === current.tid;
}

/** The resource with one of its endpoint choices applied to the draft (`vnet` = the draft as mapped). */
export function withEndpointChoice(
  resource: AzureDiscoveredResource,
  choice: AzureManagedInstanceEndpoint | undefined
): AzureDiscoveredResource {
  const picked = choice ? resource.endpointChoices?.find((entry) => entry.id === choice) : undefined;
  if (!picked) return resource;
  return { ...resource, connectionDraft: { ...resource.connectionDraft, server: picked.server, port: picked.port } };
}
